import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import removeUnobservedQueries from 'components/report-content/utils/removeUnobservedQueries';
import { reportAPI } from 'api/report';
import { AssocArray } from 'tools/types';
import useComponentReady from 'components/report-content/hooks/useComponentReady';
import { Chart as HChart, RangeSelectorOptions, XAxisOptions, YAxisOptions } from 'highcharts';
import useOnApplyAccessMapChange from 'components/report-content/hooks/useOnApplyAccessMapChange';
import useOnMeasurementTime from 'components/report-content/hooks/useOnMeasurementTime';
import Chart, { Highcharts } from 'components/chart/Chart';
import useReportContentContext from 'components/report-content/hooks/useReportContentContext';
import ReportContentSkeletonPlaceholder from 'components/report-content/utils/ReportContentSkeletonPlaceholder';
import {
    KpiChartInitialDataResponse,
    ComponentSettingsChartDataQueries,
    DisplayMask,
    ComponentUpdateProps,
    ComponentSettingsKpiChart,
    applyChartOptionsForThumbnail,
    parseDate,
    formatValueFull,
    mysql_dateformat_to_highchart_dateformat,
    IKpiResults,
    contentSettingsToComponentProperties,
    getKpiChartMeasureFieldDisplayLabel,
    IKpiAlertEvent,
    IKpiChartOverlaysSettings,
    IKpiVarianceResult,
} from '@report-content';
import { ChartOptions } from 'highcharts/highstock';
import TopControls from './parts/TopControls';
import { getKpiStoplight } from './series/getKpiStoplight';
import { getKpiAlert } from './series/getKpiAlert';
import { useTheme } from '@mui/material/styles';
import { getMeasurementSeries, IKpiMeasurePointData } from './series/getMeasurementSeries';
import {
    SeriesAlertId,
    SeriesMeasurementId,
    SeriesOverlayRightAxisId,
    SeriesPrimaryId,
    SeriesStoplightRedId,
    SeriesStoplightGreenId,
} from './series';
import TooltipContainer from 'components/chart/TooltipContainer';
import { ChartPoint } from 'components/metric/chart';
import { getMovingAverage } from './series/getMovingAverage';
import {
    getSplitCell,
    getSplitCombinations,
    ISplitCombinationsResult,
    SPLIT_MAX_COMBINATIONS,
} from './series/getSplitCombinations';
import { keepFirstRowPerDate } from './series/keepFirstRowPerDate';
import useChartColorPalette from 'components/report-content/hooks/useChartColorPalette';
import { useKpiChartOverlays } from './hooks/useKpiChartOverlays';
import { useKpiChartSummaryStats } from './hooks/useKpiChartSummaryStats';
import useBundleTranslation from 'i18n';
import { useTooltip } from './parts/useTooltip';
import IconMi from 'components/common/icon/IconMi';
import { alpha, Box, Button, IconButton, Stack, Tooltip as MuiTooltip } from '@mui/material';
import FormSwitch from 'components/common/form/layout/control/FormSwitch';
import { FormRendererAPIType } from 'components/common/form';
import OverlayMetricsPopup from './parts/overlays/OverlayMetricsPopup';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { ElementPreviewSummary } from 'app/home/tile/preview/ElementPreviewSummary';
import { MetricViewerDataType } from 'components/metric/MetricViewer';
Highcharts.AST.allowedReferences.push('data:image/svg+xml;base64,');
const noSplit: ISplitCombinationsResult = { combinations: [], limitExceeded: false };
type SeriesType = Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions | Highcharts.SeriesFlagsOptions;
function getFormattedValue(row: AssocArray<string>, field: string): string {
    return row[field + '-formatted'] ?? row[field];
}

export default function KpiChart({
    contentSettings,
    component,
    blockId,
}: ComponentUpdateProps<ComponentSettingsKpiChart>) {
    const { t } = useBundleTranslation(['components/report-content']);
    const { filtersString, elementInfo, reportOverlaysState, saveBlockOverlays } = useReportContentContext();
    // Last request to server was made with this data
    const [lastDataQueries, setDataQueries] = useState<ComponentSettingsChartDataQueries | null>(null);
    const isThumbnail = contentSettings.forScreenShot && window.location.href.includes('thumbnail');
    const dataQueryKey = `report_block_initial_${component.datasetRendererBlockComponentId}_${contentSettings.segmentValueId}`;
    const queryClient = useQueryClient();
    const resetData = () => queryClient.removeQueries({ queryKey: [dataQueryKey] });
    const { data: initialData } = useQuery<KpiChartInitialDataResponse, Error>({
        queryKey: [dataQueryKey, filtersString],
        queryFn: () => {
            setDataQueries(component.settings.dataQueries);
            return reportAPI.getComponentData(
                component.settings,
                contentSettingsToComponentProperties(contentSettings),
                filtersString,
            );
        },
    });

    useEffect(() => {
        // Remove data on unmount
        return () => removeUnobservedQueries(queryClient, [dataQueryKey]);
    }, []);

    const [data, setData] = useState<Array<AssocArray<string>>>([]);
    const [kpiData, setKpiData] = useState<Array<IKpiResults>>([]);
    const [alertsData, setAlertsData] = useState<Array<IKpiAlertEvent>>([]);
    const [kpiVarianceResult, setKpiVarianceResult] = useState<IKpiVarianceResult | null>(null);
    const [showStoplight, setShowStoplight] = useState(false);
    const [splitState, setSplitState] = useState<ISplitCombinationsResult>(noSplit);
    const [nonAggregatedMeasures, setNonAggregatedMeasures] = useState<Array<string>>([]);
    // Report-wide overlays state is owned by the viewer (ReportViewer -> BlocksContext), keyed by
    // block uid - viewer-only, undefined in editor/screenshot contexts. Derived, not local state:
    // the popup owns its own draft while open and commits back through saveBlockOverlays on close.
    const overlaySettings = useMemo(
        (): IKpiChartOverlaysSettings => (blockId ? reportOverlaysState?.[blockId] : undefined) ?? { overlays: [] },
        [reportOverlaysState, blockId],
    );
    const overlays = overlaySettings.overlays;
    // Reset all data on dataQueries change
    useEffect(() => {
        if (!initialData) {
            return;
        }
        if (
            lastDataQueries != null &&
            JSON.stringify(lastDataQueries) != JSON.stringify(component.settings.dataQueries)
        ) {
            resetData();
        }
    }, [component.settings.dataQueries]);

    useOnApplyAccessMapChange(contentSettings.applyAccessMap, resetData);
    useOnMeasurementTime(contentSettings.measurement_time, resetData);
    // Reset all data on Component Filter Change, Stoplight Change or KPI Variance Change
    const watchStoplight = useMemo(() => {
        const result: any = structuredClone(component.settings.stoplight);
        delete result.stoplightColorForHighValues;
        delete result.stoplightColorForLowValues;
        return result;
    }, [component.settings.stoplight]);
    const watchKpiVariance = useMemo(() => {
        return structuredClone(component.settings.kpiVariance);
    }, [component.settings.kpiVariance]);
    const watchSplitByFilter = useMemo(
        () => ({
            enabled: component.settings.split_by_filter_values == 'Y',
            measures: component.settings.measureFields.filter((f) => f.is_split_by_filter == 'Y').map((f) => f.uId),
        }),
        [component.settings.split_by_filter_values, component.settings.measureFields],
    );
    useEffect(() => {
        if (!initialData) {
            return;
        }
        resetData();
    }, [
        component.settings.groupFilter,
        component.settings.x_axis.field,
        JSON.stringify(watchStoplight),
        JSON.stringify(watchKpiVariance),
        JSON.stringify(watchSplitByFilter),
    ]);

    const { Tooltip, handlePointClick, handlePointMouseOver, handlePointMouseOut, pointToShow } = useTooltip();

    const chart = useRef<HChart>();
    const isChartReady = useComponentReady(chart.current);

    // "Default" falls back to the axis' own natural formatting: the primary measure
    // field's mask for the left axis (legacy behavior), no mask override for the right axis.
    const primaryMeasureField = useMemo(
        () => component.settings.measureFields.find((f) => f.is_primary == 'Y') ?? component.settings.measureFields[0],
        [component.settings.measureFields],
    );
    const leftAxisDisplayMask = useMemo(() => {
        const maskId = overlaySettings.left_axis_display_mask_id ?? primaryMeasureField?.display_mask_id;
        return contentSettings.displayMasks.find((m) => m.display_mask_id == maskId);
    }, [overlaySettings.left_axis_display_mask_id, primaryMeasureField, contentSettings.displayMasks]);

    const rightAxisDisplayMask = useMemo(() => {
        return contentSettings.displayMasks.find(
            (m) => m.display_mask_id == overlaySettings.right_axis_display_mask_id,
        );
    }, [overlaySettings.right_axis_display_mask_id, contentSettings.displayMasks]);

    const { getOverlaySeriesList, isOverlayPopupOpen, setIsOverlayPopupOpen, isOverlaysDataLoading } =
        useKpiChartOverlays({
            chart,
            overlays,
            isDataLoaded: !!initialData,
            contentSettings,
            filtersString,
            leftAxisDisplayMask,
            rightAxisDisplayMask,
            isChartReady,
            handlePointClick,
            handlePointMouseOver,
            handlePointMouseOut,
        });

    const handleRevertOverlays = () => {
        if (!blockId || !window.confirm(t('kpi_chart.revert_confirm'))) {
            return;
        }
        saveBlockOverlays?.(blockId, { overlays: [] });
    };

    useEffect(() => {
        if (!initialData) {
            return;
        }
        const deduped = keepFirstRowPerDate(
            initialData.dataResults.table,
            component.settings.measureFields,
            component.settings.x_axis.field,
        );
        setData(deduped.rows);
        setNonAggregatedMeasures(deduped.nonAggregatedMeasures);
        setKpiData(initialData.kpiResults ?? []);
        setAlertsData(initialData.alertEvents ?? []);
        setKpiVarianceResult(initialData.kpiVarianceResult ?? null);
        const splitEnabled =
            component.settings.split_by_filter_values == 'Y' &&
            component.settings.measureFields.some((f) => f.is_split_by_filter == 'Y');
        // filtersString is part of the query key, so it's the filters this data was requested with
        setSplitState(splitEnabled ? getSplitCombinations(filtersString) : noSplit);
    }, [initialData]);

    const xAxisSettingsTemplate = {
        enabled: false,
        lineWidth: 0,
        minorGridLineWidth: 0,
        lineColor: 'transparent',
        gridLineColor: 'transparent',
        labels: {
            enabled: false,
        },
        minorTickLength: 0,
        tickLength: 0,
        minPadding: 0,
        maxPadding: 0,
    };

    const yAxisSettingsTemplate = {
        opposite: false,
        minorTickLength: 0,
        tickLength: 0,
        minPadding: 0,
        maxPadding: 0,
        title: {
            text: '',
            useHTML: true,
        },
        labels: {
            // align: 'left',
        },
        plotLines: [
            {
                value: 0,
                width: 2,
                color: 'silver',
            },
        ],
    };

    const options: Omit<Highcharts.Options, 'chart rangeSelector xAxis yAxis series legend'> & {
        chart: ChartOptions;
        rangeSelector: RangeSelectorOptions;
        xAxis: XAxisOptions;
        yAxis: Array<YAxisOptions>;
        series: Array<SeriesType>;
        legend: Highcharts.LegendOptions;
    } = {
        chart: {
            animation: false,
            height: 600,
            zooming: {
                mouseWheel: {
                    enabled: false,
                },
            },
            spacingLeft: 16,
            spacingTop: 8,
        },
        tooltip: { enabled: false },
        legend: {
            enabled: !isThumbnail,
            useHTML: true,
            itemDistance: 10,
            //@ts-ignore
            custom: {
                scrollableLegendArea: {
                    minHeight: 120,
                },
            },
        },
        credits: { enabled: false },
        title: { text: '' },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        xAxis: xAxisSettingsTemplate,
        yAxis: [yAxisSettingsTemplate],
        plotOptions: {
            series: {
                animation: false,
                turboThreshold: 0,
            },
            line: {
                stickyTracking: false,
                dataGrouping: {
                    enabled: false,
                },
            },
            area: { fillOpacity: 0.75 },
            flags: {
                clip: false,
                point: {
                    events: {
                        click: function () {
                            handlePointClick(this);
                        },
                        mouseOver: function () {
                            // Fix issue with moving flags on hover
                            //@ts-ignore
                            if (this._y) {
                                this.update({}, true);
                            }
                            handlePointMouseOver(this);
                        },
                        mouseOut: () => handlePointMouseOut(),
                    },
                },
            },
        },
        navigator: { enabled: !isThumbnail },
        scrollbar: { enabled: !isThumbnail },
        series: [],
    };

    const findDisplayMask = function (display_mask_id: string) {
        return contentSettings.displayMasks.find((m) => m.display_mask_id == display_mask_id);
    };
    //
    let dm: DisplayMask | undefined = undefined;
    const yAxisFormatter = function () {
        //@ts-ignore
        const v = this.value;
        return dm && 'date' === dm?.mask_type && dm?.date_format_string > ''
            ? Highcharts.dateFormat('%m/%d/%Y', v)
            : //@ts-ignore
              formatValueFull(v, dm);
    };

    const theme = useTheme();
    const topPanelHeight = 52 + 48;
    const chartMessageHeight = 32;
    const chartMessages: Array<string> = [];
    if (nonAggregatedMeasures.length) {
        chartMessages.push(
            t('kpi_chart.non_aggregated_measure_duplicates', { measures: nonAggregatedMeasures.join(', ') }),
        );
    }
    if (splitState.limitExceeded) {
        chartMessages.push(t('kpi_chart.split_limit_exceeded', { max: SPLIT_MAX_COMBINATIONS }));
    }
    const chartColorPalette = useChartColorPalette();

    const getSeries = () => {
        const innerData = structuredClone(data);
        let xColumn = component.settings.x_axis.field;

        const seriesOptions: Array<SeriesType> = [];
        // Add Measure Series
        const primary = component.settings.measureFields.find((s) => s.is_primary == 'Y');
        // Hide series with overlay_in_chart or overlay_in_tile enabled in chart or thumbnail respectively,
        // unless it's primary and no other series
        let alwaysShowPrimary = false;
        if (contentSettings.forScreenShot && !isThumbnail) {
            alwaysShowPrimary =
                component.settings.measureFields.findIndex((field) => field.overlay_in_chart == 'Y') == -1;
        } else if (isThumbnail) {
            alwaysShowPrimary =
                component.settings.measureFields.findIndex((field) => field.overlay_in_tile == 'Y') == -1;
        }
        // A split measure draws one series per filter value combination instead of its total.
        // The backend may decline to split a measure (e.g. Aggregation "None"); without any
        // combination column in the data the measure keeps its total line.
        const splitFields = component.settings.measureFields.filter(
            (f) =>
                f.is_split_by_filter == 'Y' &&
                splitState.combinations.some((c) =>
                    innerData.some((row) => f.reference_name in (getSplitCell(row, c) ?? {})),
                ),
        );
        // getValues picks the object a series reads its valueKey (and valueKey-formatted) from
        const measureSeries: Array<{
            series: SeriesType;
            getValues: (row: AssocArray<any>) => AssocArray<any> | undefined;
            valueKey: string;
            isSplit: boolean;
        }> = [];
        // Split series take palette colors not already used by unsplit measures first;
        // only once those run out do they reuse the taken ones.
        const takenColors = new Set(
            component.settings.measureFields
                .filter((f) => !splitFields.includes(f) && f.color)
                .map((f) => f.color.toUpperCase()),
        );
        const splitColorPalette = [
            ...chartColorPalette.filter((c) => !takenColors.has(c)),
            ...chartColorPalette.filter((c) => takenColors.has(c)),
        ];
        let splitColorIndex = 0;
        component.settings.measureFields.forEach((field, index) => {
            let isVisible = true;
            if (contentSettings.forScreenShot) {
                isVisible =
                    (isThumbnail ? field.overlay_in_tile == 'Y' : field.overlay_in_chart == 'Y') ||
                    (field.is_primary == 'Y' && alwaysShowPrimary);
            }
            const measureLabel = getKpiChartMeasureFieldDisplayLabel(field);
            const baseSeries = getMeasurementSeries(field, field.show_data_labels == 'Y', index, isVisible);
            const point = {
                events: {
                    click: function (this: Highcharts.Point) {
                        handlePointClick(this);
                    },
                    mouseOver: function (this: Highcharts.Point) {
                        //@ts-ignore
                        handlePointMouseOver(this, this.series.id);
                    },
                    mouseOut: function () {
                        handlePointMouseOut();
                    },
                },
            };

            if (!splitFields.includes(field)) {
                measureSeries.push({
                    series: { ...baseSeries, point },
                    getValues: (row) => row,
                    valueKey: field.reference_name,
                    isSplit: false,
                });
                return;
            }
            splitState.combinations.forEach((combination, combinationIndex) => {
                const valueKey = field.reference_name;
                // A combination with no data is not drawn, so it must not take a palette color
                if (
                    !innerData.some(
                        (row) => row[xColumn] != null && !isNaN(parseFloat(getSplitCell(row, combination)?.[valueKey])),
                    )
                ) {
                    return;
                }
                const labels = splitFields.length > 1 ? [measureLabel, ...combination] : combination;
                measureSeries.push({
                    series: {
                        ...baseSeries,
                        id: `${SeriesMeasurementId}_${index}_split_${combinationIndex}`,
                        name: labels.join(' – '),
                        color: splitColorPalette[splitColorIndex++ % splitColorPalette.length],
                        data: [],
                        point,
                    },
                    valueKey,
                    getValues: (row) => getSplitCell(row, combination),
                    isSplit: true,
                });
            });
        });
        let maxY = Number.NEGATIVE_INFINITY,
            minY = Number.POSITIVE_INFINITY;
        innerData.forEach((row) => {
            const xVal = row[xColumn];
            measureSeries.forEach(({ series, getValues, valueKey }) => {
                const values = getValues(row);
                const val = parseFloat(values?.[valueKey]);
                if (!values || isNaN(val) || null == xVal) {
                    return;
                }
                if (val > maxY) {
                    maxY = val;
                }
                if (val < minY) {
                    minY = val;
                }

                series.data!.push({
                    x: parseDate(xVal).getTime(),
                    y: val,
                    custom: {
                        color: series.color as string,
                        name_f: series.name ?? '',
                        x_f: getFormattedValue(row, xColumn),
                        y_f: getFormattedValue(values, valueKey),
                    } as IKpiMeasurePointData,
                });
            });
        });
        seriesOptions.push(...measureSeries.map((m) => m.series));
        const hasSplitSeries = measureSeries.some((m) => m.isSplit);
        // Add Overlay Series
        seriesOptions.push(...getOverlaySeriesList());
        // Add Stoplight Series
        if (component.settings.stoplight.includeStoplight == 'Y') {
            const stoplight = getKpiStoplight(
                component.settings.stoplight,
                kpiData,
                minY,
                maxY,
                'calendar_date',
                primary ? getKpiChartMeasureFieldDisplayLabel(primary) : '',
                t,
                showStoplight,
            );
            seriesOptions.push(stoplight[0]);
            seriesOptions.push(stoplight[1]);
        }
        // Add Moving Average Series
        // Hidden until for 7.3.0
        // if (component.settings.kpiVariance.includeKpiVariance == 'Y') {
        //     const deviation = getMovingAverage(component.settings.kpiVariance, kpiData, 'calendar_date', t, theme);
        //     seriesOptions.push(deviation);
        // }
        // Add Alert Series
        const primarySeries = seriesOptions.find((s) => s.id == SeriesPrimaryId) ?? seriesOptions[0];
        const alertSeries = getKpiAlert(
            alertsData,
            primarySeries as Highcharts.SeriesLineOptions,
            component.settings.x_axis.field,
            theme,
        );
        // Alert flags belong to the total line, which is not drawn while the chart is split
        if (alertSeries?.data?.length && !hasSplitSeries) {
            seriesOptions.push(alertSeries);
            // Fix issue with first hover on alert points
            setTimeout(() => {
                if (chart.current) {
                    try {
                        chart.current.redraw();
                    } catch (e) {}
                }
            }, 1000);
        }
        // Show marker when series has only 1 point
        seriesOptions.forEach((s) => {
            if (s.data?.length == 1) {
                (s as Highcharts.SeriesLineOptions).marker = { enabled: true, radius: 4 };
            }
        });
        // Disable legend if only 1 series
        if (seriesOptions.filter((s) => s.showInLegend !== false).length == 1) {
            options.legend.enabled = false;
        }
        return seriesOptions;
    };

    const chartOptions = useMemo(() => {
        if (isThumbnail) {
            applyChartOptionsForThumbnail(options);
        } else {
            options.chart.height =
                component.settings.height - topPanelHeight - chartMessages.length * chartMessageHeight;
        }

        if (!isThumbnail && component.settings.x_axis.title) {
            options.xAxis.title = { text: component.settings.x_axis.title, useHTML: true };
        }

        const dmXAxis = findDisplayMask(component.settings.x_axis.display_mask_id);
        options.xAxis.labels = {
            enabled: !isThumbnail,
            formatter: dmXAxis
                ? function (): string {
                      const v = Number(this.value);
                      if (dmXAxis && 'date' === dmXAxis.mask_type && dmXAxis.date_format_string > '') {
                          return isNaN(v)
                              ? (this.value as string)
                              : Highcharts.dateFormat(mysql_dateformat_to_highchart_dateformat(dmXAxis), v);
                      }
                      return Number(this.value) == this.value
                          ? Highcharts.dateFormat('%m/%d/%Y', v)
                          : (this.value as string);
                  }
                : undefined,
        };

        if (component.settings?.y_axis?.length) {
            options.yAxis[0]!.title!.text = component.settings.y_axis[0].title;
            options.yAxis[0]!.labels!.formatter = yAxisFormatter;
            dm = findDisplayMask(primaryMeasureField?.display_mask_id ?? '');
            if (dm && 'date' == dm.mask_type && dm.date_format_string > '') {
                options.yAxis[0].type = 'datetime';
            }
        }

        options.yAxis.push({
            id: SeriesOverlayRightAxisId,
            opposite: true,
            visible: false,
            minorTickLength: 0,
            tickLength: 0,
            minPadding: 0,
            maxPadding: 0,
            title: { text: '', useHTML: true },
        });

        options.series = getSeries();
        return options;
        // overlays/overlaysData are deliberately excluded - useKpiChartOverlays applies those
        // changes imperatively onto the live chart instead, to avoid the full destroy+recreate
    }, [data, kpiData, splitState, nonAggregatedMeasures, JSON.stringify(component.settings)]);

    useEffect(() => {
        const defaultSpacingTop = 8;
        const updateSpacingTop = (value: number) => {
            if (!chart.current) {
                return;
            }
            const current = (chart.current.options.chart?.spacingTop as number) ?? defaultSpacingTop;
            if (current != value) {
                chart.current.update({ chart: { spacingTop: value } });
            }
        };

        requestAnimationFrame(() => {
            if (!chart.current?.series) {
                return;
            }

            if (!alertsData.length) {
                updateSpacingTop(defaultSpacingTop);
                return;
            }

            const primarySeries = chart.current.series.find((s) => s.options.id == SeriesPrimaryId);
            if (!primarySeries) {
                return;
            }

            const alertSeries = chart.current.series.find((s) => s.options.id == SeriesAlertId);
            const alertXTimes = new Set<number>(alertSeries?.points.map((p) => p.x) ?? []);

            let minPlotY = Infinity;
            primarySeries.points.forEach((point) => {
                if (alertXTimes.has(point.x)) {
                    const y = point.plotY;
                    if (y !== undefined && y < minPlotY) {
                        minPlotY = y;
                    }
                }
            });

            if (minPlotY === Infinity) {
                return;
            }

            // flags y:-32, icon top:-5 → icon top is 37px above bar top
            // plotY is relative to chart.plotTop, so icon top in SVG = plotTop + plotY - 37
            const iconTopInSVG = chart.current.plotTop + minPlotY - 37;
            if (iconTopInSVG < 0) {
                const current = (chart.current.options.chart?.spacingTop as number) ?? defaultSpacingTop;
                updateSpacingTop(current + Math.ceil(-iconTopInSVG) + 4);
            } else {
                updateSpacingTop(defaultSpacingTop);
            }
        });
    }, [alertsData, data, kpiData, JSON.stringify(component.settings)]);

    useEffect(() => {
        if (!chart.current) {
            return;
        }
        try {
            [SeriesStoplightRedId, SeriesStoplightGreenId].forEach((id) => {
                const series = chart.current?.get(id) as Highcharts.Series | undefined;
                //@ts-ignore
                series?.update({ visible: showStoplight, showInLegend: showStoplight }, false);
            });
            chart.current.redraw();
        } catch (e) {}
    }, [showStoplight, chart.current]);

    const [selectedRange, setSelectedRange] = useState<string>('all');
    const [isSummaryVisible, setIsSummaryVisible] = useState(false);

    const handleToggleSummaryVisibility = (isOpen: boolean) => {
        setIsSummaryVisible(isOpen);
        if (blockId) {
            reportAPI.setKpiChartSummaryState(contentSettings.elementId, blockId, isOpen);
        }
    };

    const summaryStats = useKpiChartSummaryStats({
        data,
        kpiData,
        measureFields: component.settings.measureFields,
        xAxisField: component.settings.x_axis.field,
        kpiVariance: component.settings.kpiVariance,
        kpiVarianceResult,
        displayMasks: contentSettings.displayMasks,
        elementId: contentSettings.elementId,
    });

    const iconButtonSx = { p: '6px', borderRadius: '3px' };
    const hasSummary =
        contentSettings.context == 'view' &&
        Boolean(elementInfo) &&
        (component.settings.show_summary_in_viewer ?? 'Y') == 'Y' &&
        !!blockId;

    const { data: savedSummaryVisible, isFetched: isSummaryStateFetched } = useQuery<boolean, Error>({
        queryKey: [`report_summary_state_${contentSettings.elementId}_${blockId}`],
        queryFn: () => reportAPI.getKpiChartSummaryState(contentSettings.elementId, blockId!),
        enabled: hasSummary,
    });
    useEffect(() => {
        if (savedSummaryVisible !== undefined) {
            setIsSummaryVisible(savedSummaryVisible);
        }
    }, [savedSummaryVisible]);
    const isSummaryStateReady = !hasSummary || isSummaryStateFetched;

    return initialData && isSummaryStateReady ? (
        <Box style={{ overflow: 'hidden', maxHeight: component.settings.height + 'px', position: 'relative' }}>
            {!contentSettings.forScreenShot && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        minHeight: '48px',
                        px: 2,
                        borderBottom: '1px solid',
                        borderColor: alpha(theme.palette.text.primary, 0.08),
                    }}
                >
                    <Box
                        sx={{
                            fontWeight: 600,
                            fontSize: '14px',
                            color: theme.palette.primary.main,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {component.settings.title}
                    </Box>
                    {contentSettings.context == 'view' && (
                        <Stack direction="row" alignItems="center" sx={{ gap: '12px', flexShrink: 0 }}>
                            <>
                                <Stack direction="row" alignItems="center">
                                    <MuiTooltip title={t('kpi_chart.overlays')}>
                                        <IconButton sx={iconButtonSx} onClick={() => setIsOverlayPopupOpen(true)}>
                                            <IconMi icon="layers" fontSize="16" />
                                        </IconButton>
                                    </MuiTooltip>
                                    {false && (
                                        <MuiTooltip title={t('kpi_chart.add_event')}>
                                            <IconButton sx={iconButtonSx}>
                                                <IconMi icon="add-event" fontSize="16" />
                                            </IconButton>
                                        </MuiTooltip>
                                    )}
                                    {false && (
                                        <MuiTooltip title={t('kpi_chart.add_annotation')}>
                                            <IconButton sx={iconButtonSx}>
                                                <IconMi icon="add-annotation" fontSize="16" />
                                            </IconButton>
                                        </MuiTooltip>
                                    )}
                                </Stack>
                                <Box
                                    sx={{
                                        width: '1px',
                                        height: '16px',
                                        bgcolor: alpha(theme.palette.text.primary, 0.4),
                                    }}
                                />
                                <MuiTooltip title={t('kpi_chart.revert_changes')}>
                                    <IconButton sx={iconButtonSx} onClick={handleRevertOverlays}>
                                        <IconMi icon="rotate-ccw" fontSize="16" />
                                    </IconButton>
                                </MuiTooltip>
                            </>
                        </Stack>
                    )}
                </Stack>
            )}
            <Stack direction="row">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    {!contentSettings.forScreenShot && (
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <TopControls
                                chart={chart.current}
                                selectedRange={selectedRange}
                                onChange={(range: string) => setSelectedRange(range)}
                            />
                            <Stack direction="row" alignItems="center" sx={{ gap: '12px', flexShrink: 0 }}>
                                {component.settings.stoplight.includeStoplight == 'Y' && (
                                    <FormSwitch
                                        controlProps={{
                                            form: {} as FormRendererAPIType,
                                            name: 'show_stoplight',
                                            label: t('kpi_chart.show_stoplight'),
                                            checked: showStoplight,
                                            onChange: () => setShowStoplight((prev) => !prev),
                                            disabled: false,
                                            reversed: false,
                                        }}
                                    />
                                )}
                                {!isSummaryVisible && hasSummary && (
                                    <>
                                        {component.settings.stoplight.includeStoplight == 'Y' && (
                                            <Box
                                                sx={{
                                                    width: '1px',
                                                    height: '16px',
                                                    bgcolor: alpha(theme.palette.text.primary, 0.4),
                                                }}
                                            />
                                        )}
                                        <Button
                                            variant="outlined"
                                            startIcon={<IconMi icon="chevron-left" />}
                                            onClick={() => handleToggleSummaryVisibility(true)}
                                            sx={{ mr: 2 }}
                                        >
                                            {t('kpi_chart.show_summary')}
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    )}
                    {chartMessages.map((message) => (
                        <MuiTooltip key={message} title={message} placement="auto-start">
                            <Box
                                sx={{
                                    height: chartMessageHeight,
                                    lineHeight: `${chartMessageHeight}px`,
                                    px: 2,
                                    fontSize: '12px',
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {message}
                            </Box>
                        </MuiTooltip>
                    ))}
                    <Chart
                        immutable={true}
                        chartOptions={chartOptions}
                        afterChartCreated={(c: HChart) => (chart.current = c)}
                    />
                    {chart.current && Tooltip && (
                        <TooltipContainer point={{ ...pointToShow, metadata: {} } as ChartPoint} chart={chart.current}>
                            {(closeComponent) => Tooltip}
                        </TooltipContainer>
                    )}
                    {isOverlaysDataLoading && <LoadingPlaceholder relativeToParent />}
                </Box>
                {isSummaryVisible && hasSummary && elementInfo && (
                    <ElementPreviewSummary
                        elementInfo={elementInfo}
                        viewerRequestExtraData={{ metrics: summaryStats ? [summaryStats] : [] } as MetricViewerDataType}
                        onToggleVisibility={() => handleToggleSummaryVisibility(false)}
                        isVisible={isSummaryVisible}
                        isReportMetric={true}
                    />
                )}
            </Stack>
            {isOverlayPopupOpen && (
                <OverlayMetricsPopup
                    isOpen={isOverlayPopupOpen}
                    setIsOpen={setIsOverlayPopupOpen}
                    elementId={contentSettings.elementId}
                    overlays={overlays}
                    measureFields={component.settings.measureFields}
                    displayMasks={contentSettings.displayMasks}
                    leftAxisDisplayMaskId={overlaySettings.left_axis_display_mask_id}
                    rightAxisDisplayMaskId={overlaySettings.right_axis_display_mask_id}
                    onSave={blockId ? (newSettings) => saveBlockOverlays?.(blockId, newSettings) : undefined}
                />
            )}
        </Box>
    ) : (
        <ReportContentSkeletonPlaceholder height={component.settings.height} />
    );
}
